import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Input,
  Stack,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../../../../store';
import Grid from '@mui/material/Grid2';
import { getNumberValue } from '../../../../../utils';
import CurrencySelect from '../../../../../components/shared/CurrencySelect';
import CusInput from '../../../../../components/material-ui/CusInput';
import CusCheckbox from '../../../../../components/material-ui/CusCheckbox';
import ShowContainer from '../../../../../components/material-ui/ShowContainer';
import MyTableCell from '../../../../../components/material-ui/adaptive/MyTableCell';
import CusLabelSelect from '../../../../../components/material-ui/CusLabelSelect';
import BtnPlus from '../../../../../components/bonus/ui/BtnPlus';
import BtnTrash from '../../../../../components/bonus/ui/BtnTrash';
import { styleCellTitle } from '../../../../../components/bonus/ui/styles';
import FlexContainer from '../../../../../components/material-ui/adaptive/FlexContainer';
import useBootstrapSize from '../../../../../hooks/useBootstrapSize';
import CusButton from '../../../../../components/material-ui/CusButton';

const paymentDays = [
  { label: 'Not selected', value: '0' },
  { label: 'Mon', id: 1, value: '1' },
  { label: 'Tue', id: 2, value: '2' },
  { label: 'Wed', id: 3, value: '3' },
  { label: 'Thu', id: 4, value: '4' },
  { label: 'Fri', id: 5, value: '5' },
  { label: 'Sat', id: 6, value: '6' },
  { label: 'Sun', id: 7, value: '7' }
];

export default function CashbackDay({
  activePackage,
  groupId,
  activeDay,
  changePayoutDays,
  changeHoldDay,
  changeToDeposit,
  changeFieldPackage,
  changeAutoActive,
  selectCurrency,
  setSelectCurrency,
  onCopyToDays,
  onDeleteRow
}) {
  const { t } = useTranslation();
  const { isMobile } = useBootstrapSize();
  const [copyDays, setCopyDays] = useState([]);
  const currencies = activePackage?.values || {};
  const payoutDays = String(activePackage?.payoutWeekDay) || '0';
  const holdDays = activePackage?.holdDays;
  const toDeposit = activePackage?.toDeposit;
  const autoActive = activePackage?.options?.[selectCurrency]?.userActivate;
  const availableCurrencies = Object.keys(currencies).length;

  const stepsCurrency = currencies?.[selectCurrency];

  const copyDaysOptions = useMemo(() => {
    const copyDaysOptions = [...paymentDays];
    copyDaysOptions.unshift();

    return copyDaysOptions.filter((el, indx) => el.id - 1 != activeDay);
  }, [paymentDays, activeDay]);

  const onPlusClick = (currency, indx) => {
    changeFieldPackage(activeDay, currency, indx, 'minDeposit', 1);
    changeFieldPackage(activeDay, currency, indx, 'maxDeposit', 1);
    changeFieldPackage(activeDay, currency, indx, 'percent', 1);
    changeFieldPackage(activeDay, currency, indx, 'wager', 1);
    changeFieldPackage(activeDay, currency, indx, 'cashout', 1);
  };

  const changeField = (indx, name, value) => {
    let newValue = getNumberValue(value);

    if (['maxDeposit', 'cashout'].includes(name)) {
      newValue = value ? Math.abs(value) : null;
    }

    changeFieldPackage(activeDay, selectCurrency, indx, name, newValue);
  };

  useEffect(() => {
    setCopyDays([]);
  }, [activeDay]);

  return (
    <Box mb={2}>
      <TableContainer sx={{ minWidth: { sm: '350px' } }}>
        <Table>
          <TableHead>
            <TableRow>
              <MyTableCell style={styleCellTitle}>{t('Currency')}</MyTableCell>
              <MyTableCell style={styleCellTitle}>{t('Minimum deposit amount')}</MyTableCell>
              <MyTableCell style={styleCellTitle}>{t('Maximum deposit amount')}</MyTableCell>
              <MyTableCell style={styleCellTitle}>{t('% cashback')}</MyTableCell>
              <MyTableCell style={styleCellTitle}>{t('Bonus wager')}</MyTableCell>
              <MyTableCell style={styleCellTitle}>{t('Bonus cashout')}</MyTableCell>
              <MyTableCell style={styleCellTitle}>
                <CurrencySelect
                  setValue={setSelectCurrency}
                  value={selectCurrency}
                  id={'currencies_cashback'}
                />
              </MyTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {stepsCurrency?.map((curStep, indx) => (
              <ShowContainer condition={curStep} key={indx}>
                <TableRow>
                  <MyTableCell>
                    <b>{selectCurrency}</b>
                  </MyTableCell>
                  <MyTableCell>
                    <Input
                      value={curStep?.minDeposit}
                      onChange={(e) => changeField(indx, 'minDeposit', e.target.value)}
                      type="number"
                      inputProps={{ min: 0 }}
                    />
                  </MyTableCell>
                  <MyTableCell>
                    <Input
                      value={curStep?.maxDeposit}
                      onChange={(e) => changeField(indx, 'maxDeposit', e.target.value)}
                      type="number"
                      inputProps={{ min: 1 }}
                    />
                  </MyTableCell>
                  <MyTableCell>
                    <Input
                      value={curStep?.percent}
                      onChange={(e) => changeField(indx, 'percent', +e.target.value)}
                      type="number"
                      inputProps={{ min: 0 }}
                    />
                  </MyTableCell>
                  <MyTableCell>
                    <Input
                      value={curStep?.wager}
                      onChange={(e) => changeField(indx, 'wager', +e.target.value)}
                      type="number"
                      inputProps={{ min: 0 }}
                    />
                  </MyTableCell>
                  <MyTableCell>
                    <Input
                      value={curStep?.cashout}
                      onChange={(e) => changeField(indx, 'cashout', e.target.value)}
                      type="number"
                      inputProps={{ min: 0 }}
                    />
                  </MyTableCell>

                  <MyTableCell>
                    <ShowContainer condition={indx}>
                      <BtnTrash onDelete={() => onDeleteRow(activeDay, selectCurrency, indx)} />
                    </ShowContainer>
                  </MyTableCell>
                </TableRow>
              </ShowContainer>
            ))}
            <TableRow>
              <MyTableCell></MyTableCell>
              <MyTableCell></MyTableCell>
              <MyTableCell></MyTableCell>
              <MyTableCell></MyTableCell>
              <MyTableCell></MyTableCell>
              <MyTableCell></MyTableCell>
              <MyTableCell>
                <BtnPlus
                  onPlusClick={() => onPlusClick(selectCurrency, stepsCurrency?.length || 0)}
                />
              </MyTableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <ShowContainer condition={availableCurrencies}>
        <Box mt={2}>
          <Grid container>
            <Grid size={{ xs: 12, lg: 2, sm: 3 }}>
              <CusInput
                label={'Hold cashback crediting for days'}
                type="number"
                value={holdDays}
                onChange={(e) => changeHoldDay(activeDay, getNumberValue(e.target.value))}
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid size={{ xs: 12, lg: 8, sm: 6 }} className="gap" />
            <Grid size={{ xs: 12, lg: 2, sm: 3 }}>
              <Stack display={'flex'} flexDirection={'row'} gap={1}>
                <CusLabelSelect
                  id={'payout_days'}
                  label={'Payout by days'}
                  options={paymentDays}
                  value={payoutDays}
                  onChange={(val) => changePayoutDays(activeDay, val)}
                />
              </Stack>
            </Grid>
          </Grid>
          <Stack gap={1}>
            <FlexContainer>
              <CusCheckbox
                label="Credit to deposit balance"
                checked={toDeposit}
                onChange={() => changeToDeposit(activeDay, !toDeposit)}
              />
              <CusCheckbox
                label="Require Accept' for the client to receive the bonus."
                checked={autoActive}
                onChange={() => changeAutoActive(activeDay, !autoActive)}
              />
            </FlexContainer>

            <Stack gap={1} flexDirection={'row'}>
              <CusLabelSelect
                label={'Copy to'}
                isRow={!isMobile}
                options={copyDaysOptions}
                multi
                value={copyDays}
                onChange={(val) => {
                  console.log('val', val);
                  setCopyDays(val);
                }}
                toolTipText="You must save after completion."
              />

              <CusButton
                text="Perform"
                onClick={() => onCopyToDays(activeDay, copyDays)}
                variant="outlined"
                sx={{ px: 5 }}
              />
            </Stack>
          </Stack>
        </Box>
      </ShowContainer>
    </Box>
  );
}
